import { Injectable, Signal } from "@angular/core";
import { CalendarShiftModel } from "@m481/mp-sdk-ng";
import { IListDataResponse } from "@m481/mp-sdk-ng/lib/models/reponses/list-data.response";
import { Observable, of, tap } from "rxjs";

import { AppConstants } from "@mp/app.constants";
import { CalendarShiftEntity, CalendarShiftEntityHelper } from "@mp/common/entities/calendar-shift.entity";
import { DateUtil } from "@mp/common/helpers/date-util";
import { IWeek } from "@mp/common/types/date.types";
import { BaseStoreService } from "./base-store.service";

@Injectable({
    providedIn: "root"
})
export class CalendarShiftStoreService extends BaseStoreService<CalendarShiftEntity, CalendarShiftModel> {
    constructor() {
        super({
            storageKey: AppConstants.StorageKeys.STORE.CALENDAR_SHIFT
        });
        this.init();
    }

    /*
     * Selectors
     */

    public selectCalendarShiftsByDateRange(week: IWeek): Signal<Array<CalendarShiftEntity>> {
        return this.selectWhere((cs: CalendarShiftEntity) => DateUtil.isDateInRange(cs.startAt, week));
    }

    /* Update */

    public assignUsers(id: string, users: Array<string>): Observable<boolean> {
        return this.mpSdkService.assignUsersToCalendarShift(id, users).pipe(
            tap(isOk => {
                this.log.debug("updated entity in backend", isOk);
                this.storeUpdateEntity(id, { assignedUsers: users });
                this.localStoragePersistStoreEntities();
            })
        );
    }

    /*
     * Base store service SDK methods implementation
     */

    protected mapEntityToModel(entity: CalendarShiftEntity): CalendarShiftModel {
        return CalendarShiftEntityHelper.toModel(entity);
    }

    protected mapModelToEntity(model: CalendarShiftModel): CalendarShiftEntity {
        return CalendarShiftEntityHelper.fromModel(model);
    }

    protected sdkGet$(): Observable<IListDataResponse<CalendarShiftModel>> {
        return this.mpSdkService.getCalendarShifts(this.getSelectedWeekDateRange());
    }

    protected sdkCreate$(model: CalendarShiftModel): Observable<CalendarShiftModel> {
        return of(model);
    }

    protected sdkUpdate$(model: CalendarShiftModel): Observable<CalendarShiftModel> {
        return of(model);
    }

    protected sdkDelete$(id: string): Observable<boolean> {
        return this.mpSdkService.deleteCalendarShift(id);
    }

    private getSelectedWeekDateRange(): Array<number> {
        const selectedWeek = this.storageService.getItem(AppConstants.StorageKeys.USER.WEEK);
        const weekReferenceDate = selectedWeek ? new Date(selectedWeek) : new Date();
        const week = DateUtil.calculateWeekStartAndEndDates(weekReferenceDate);
        const weekStart = week.start instanceof Date ? week.start : new Date(week.start);
        const weekEnd = week.end instanceof Date ? week.end : new Date(week.end);
        return [weekStart.getTime(), weekEnd.getTime()];
    }
}
